// ApiKeyCreate.jsx
import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import update from 'react-addons-update'

import {
  FormattedMessage,
  injectIntl,
} from 'react-intl'

import {
  createApiKey,
} from '../../../actions/actions'

import {
  CapitalButton,
} from '../Navigation/Buttons'

import {
  SelectInput,
  TextInput,
} from '../Inputs/Inputs'

import AppStore from '../../../stores/AppStore'

import styles from '../../../../css/home.module.css'
import styles2 from '../../../../css/settings.module.css'

const copyTextToClipboard = function copyTextToClipboard (text) {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text)
  }
  return false
}

const ApiKeyCreate = ({ intl: { formatMessage }, customerID }) => {
  const navigate = useNavigate()

  const [level, setLevel] = useState(0)
  const [apiPermission, setApiPermission] = useState('20')
  const [invalid, setInvalid] = useState(true)
  const [saved, setSaved] = useState(false)
  const [apiKeyData, setApiKeyData] = useState({ name: '' })

  const childChange = useCallback((event) => {
    if (event.target.name === 'apiKeyData') {
      setApiKeyData(prev => ({ ...prev, name: event.target.value }))
    } else {
      handleChange(event.target.name, event.target.value)
    }
  }, [])

  const handleChange = useCallback((field, value, callback) => {
    const newValue = value
    const newState = update({ level, apiPermission, invalid, saved }, { [field]: { $set: newValue } })

    if (field === 'level') setLevel(newValue)
    if (field === 'apiPermission') setApiPermission(newValue)
    if (field === 'invalid') setInvalid(newValue)
    if (field === 'saved') setSaved(newValue)

    if (callback) callback()
    validate()
  }, [level, apiPermission, invalid, saved])

  const validate = useCallback((callback) => {
    const newState = { invalid, saved }

    if (typeof apiKeyData.name === 'string' && apiKeyData.name.length > 0) {
      newState.invalid = false
    } else {
      newState.invalid = true
    }

    if (saved === 'error') {
      newState.saved = false
    }

    setInvalid(newState.invalid)
    setSaved(newState.saved)

    if (callback) callback()
  }, [apiKeyData.name, invalid, saved])

  // Validate when apiKeyData changes
  useEffect(() => {
    validate()
  }, [apiKeyData.name, validate])

  const callBack = useCallback((data) => {
    const { application } = data
    setApiKeyData(application)
    setSaved(true)
  }, [])

  const saveButtonFunction = useCallback(() => {
    const customerIDFromStore = AppStore.getCustomer().id

    createApiKey(
      {
        customerID: customerIDFromStore,
        name: apiKeyData.name,
        permissions: { level: parseInt(apiPermission, 10) },
      },
      callBack,
      (error) => {
        if (error) {
          setSaved('error')
        }
      }
    )
  }, [apiKeyData.name, apiPermission, callBack])

  const backToApiKeys = useCallback(() => {
    navigate(`/support/${customerID}/apiKeys`)
  }, [navigate, customerID])

  let saveButtonStyle = `${styles.saveButton} saveButton`

  if (invalid) {
    saveButtonStyle = `${styles.saveButton} saveButton`
  } else if (saved === true) {
    saveButtonStyle = `${styles.saveButton} saveButton ${styles.saveButtonSuccess}`
  } else if (saved === 'error') {
    saveButtonStyle = `${styles.saveButton} saveButton ${styles.saveButtonError}`
  }

  return (
    <>
      <div className={styles2.apiItemWrapper}>
        {!saved
          ? (
            <div className={`${styles2.apiContainer} ${styles2.apiContainerCreate}`}>
              <section className={styles.userInput}>
                <FormattedMessage
                  id='support.api.apiname'
                  defaultMessage='API Key Name'
                  description='API Key Name label'
                  tagName='h5'
                />
                <div className={styles.userInputValueEdit}>
                  <TextInput
                    field='apiKeyData'
                    value={apiKeyData.name}
                    childChange={childChange}
                  />
                </div>
              </section>
              <section>
                <FormattedMessage
                  id='support.api.permissionLevel'
                  defaultMessage='Permission Level'
                  description='Permission Level label'
                  tagName='h5'
                />
                <div className={styles.userInputValueEdit}>
                  <SelectInput
                    value={apiPermission}
                    field='apiPermission'
                    childChange={childChange}
                    options={[
                      {
                        value: '20',
                        name: formatMessage({
                          id: 'support.api.permission.administrator',
                          defaultMessage: 'Administrator',
                          description: 'English language selection',
                        }),
                      },
                      {
                        value: '10',
                        name: formatMessage({
                          id: 'support.api.permission.manager',
                          defaultMessage: 'Manager',
                          description: 'Manager selection',
                        }),
                      },
                    ]}
                    style={styles.selectInput}
                  />
                </div>
              </section>
            </div>
            )
          : (
            <div className={`${styles2.apiContainer} ${styles2.apiContainerInfo}`}>
              <FormattedMessage
                id='support.api.apiname'
                defaultMessage='API Key Name'
                description='API Key Name label'
                tagName='h5'
              />
              <div className={styles2.keyInfo}>
                <h3>{apiKeyData.name}</h3>
              </div>

              <FormattedMessage
                id='support.api.apiKeyId'
                defaultMessage='API Key ID'
                description='API Key ID label'
                tagName='h5'
              />
              <div className={styles2.keyInfo}>
                <h3>{apiKeyData.client_id}</h3>
              </div>
              <button
                type='button'
                onClick={() => { copyTextToClipboard(apiKeyData.client_id) }}
                className={styles2.copyButton}
              >
                Copy
              </button>

              <FormattedMessage
                id='support.api.apiKeySecret'
                defaultMessage='API Key Secret'
                description='API Key Secret label'
                tagName='h5'
              />

              <FormattedMessage
                id='support.api.apiKeySecretWarning'
                defaultMessage='API Key Secret will only be shown this one time. Please take time to record it.'
                description='API Key Secret Warning label'
                tagName='p'
              />
              <div className={styles2.keyInfo}>
                <h3>{apiKeyData.secret}</h3>
              </div>
              <button
                type='button'
                onClick={() => { copyTextToClipboard(apiKeyData.secret) }}
                className={styles2.copyButton}
              >
                Copy
                <FormattedMessage
                  id='support.api.copy'
                  defaultMessage='Copy'
                  description='Copy label'
                />
              </button>
            </div>
            )}
      </div>

      {!saved
        ? (
          <CapitalButton
            text={formatMessage({
              id: 'general.submit',
              defaultMessage: 'Submit',
              description: '',
            })}
            buttonFunction={saveButtonFunction}
            hSize='h2'
            style={saveButtonStyle}
            disabled={invalid}
          />
          )
        : (
          <CapitalButton
            text={formatMessage({
              id: 'general.finished',
              defaultMessage: 'Finished',
              description: '',
            })}
            buttonFunction={backToApiKeys}
            hSize='h2'
            style={`${styles.saveButton} saveButton`}
            disabled={invalid}
          />
          )}
    </>
  )
}

ApiKeyCreate.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  customerID: PropTypes.string.isRequired,
}

export default injectIntl(ApiKeyCreate)
